import React, { useState } from 'react';
import cc from 'classcat';
import Spinner from '@atlaskit/spinner';
import { captureException, setExtra } from '@sentry/core';
import { Formik, FormikHelpers, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { Button } from '../../index';
import FormikField from '../../forms/FormikField';
import { useStore } from '../../../hooks';
import { trackEvent } from '../../../lib/GA';
import FreeformHelper from '../../../lib/FreeformHelper';
import { getFormFieldHandles } from './freeformFieldPresence';

import styles from './styles.module.scss';

// Optional, per-form "resident" fields (SAJOB-6530). These are only rendered/validated/posted
// when the current form's Freeform layout actually contains the matching handle — see
// freeformFieldPresence.ts and local-review/SAJOB-6530-freeform-resident-fields.md.
// IS_RESIDENT_REQUIRED_WHEN_PRESENT / STAY_INVOLVED_REQUIRED below are pending client answers
// (spec §6, open questions 1 & 3); flip them if/when Anjela answers differently — no other
// code changes needed.
const IS_RESIDENT_REQUIRED_WHEN_PRESENT = true; // pending client answers; remove once confirmed
const STAY_INVOLVED_REQUIRED = false; // pending client answers; remove once confirmed
// When the form has projectConsent, Anjela's statement REPLACES the generic privacy checkbox
// (rather than adding a second one) and is required, reusing the `privacy` field's checkbox,
// validation and payload slot — see StepPrivacy below. This replacement (not a second, separate
// checkbox) was approved internally (Strange Animals, 30 Sep 2026); pending client confirmation.

const IS_RESIDENT_LABEL = 'Are you an Amana Living Meadow Springs Village resident?';
const VILLA_NUMBER_LABEL = 'Villa Number';
const STAY_INVOLVED_LABEL = 'How would you like to stay involved? (Select all that apply)';
const PROJECT_CONSENT_STATEMENT = 'I am happy for Amana Living to use the contact details provided above to send me information and updates relating to the proposed Meadow Springs development and associated resident engagement activities.';
const PROJECT_CONSENT_CLARIFICATION = 'Your details will only be used for communications relating to the Meadow Springs project and associated engagement activities.';

const STAY_INVOLVED_OPTIONS = [
  'Project updates and progress',
  'Upcoming open days or information sessions',
  'Future consultation and engagement opportunities',
];

interface ResidentFieldPresence {
  isResident: boolean;
  villaNumber: boolean;
  stayInvolved: boolean;
  projectConsent: boolean;
}

const getResidentFieldPresence = (freeformObj?: FreeformFormInterface | null): ResidentFieldPresence => {
  const handles = getFormFieldHandles(freeformObj);
  return {
    isResident: handles.has('isResident'),
    villaNumber: handles.has('villaNumber'),
    stayInvolved: handles.has('stayInvolved'),
    projectConsent: handles.has('projectConsent'),
  };
};

const getConnectionOptions = (locationName: string) => [
  'I live next to or opposite the site',
  `I live elsewhere in ${locationName}`,
  'I live in a nearby suburb',
  'I live in a local retirement living or aged care community',
  'I work for a local retirement living or aged care organisation',
  'I own a local business',
  'I represent a local community group, club or school',
  'I\'m interested in future accommodation (retirement living)',
  'I\'m interested in future accommodation (residential aged care)',
];

const INTEREST_OPTIONS = [
  'Receive project updates',
  'Be invited to engagement activities',
  'Be notified if/when retirement living accommodation becomes available',
  'Be notified if/when residential aged\u2011care accommodation becomes available',
];

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  connection: string[];
  connectionOther: string;
  interests: string[];
  privacy: boolean;
  isResident: string;
  villaNumber: string;
  stayInvolved: string[];
}

// Steps (and their per-step Yup schema) are built from the current form's field presence,
// so validation only ever applies to fields the current ICC landing page's Freeform form
// actually has (e.g. Menora keeps its original 4-step schema untouched).
const getSteps = (fieldPresence: ResidentFieldPresence) => [
  {
    title: 'Your Details',
    subtitle: 'Step 1 of 4',
    fields: [
      'firstName',
      'lastName',
      'email',
      ...(fieldPresence.isResident ? ['isResident'] : []),
      ...(fieldPresence.villaNumber ? ['villaNumber'] : []),
    ],
    schema: Yup.object().shape({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string().email('Please enter a valid email').required('Email is required'),
      ...(fieldPresence.isResident && IS_RESIDENT_REQUIRED_WHEN_PRESENT ? {
        isResident: Yup.string().oneOf(['Yes', 'No'], 'Please select an option').required('Please let us know if you are a Meadow Springs Village resident'),
      } : {}),
      ...(fieldPresence.villaNumber ? {
        villaNumber: Yup.string().when('isResident', {
          is: 'Yes',
          // .trim() so whitespace-only input doesn't pass required().
          then: Yup.string().trim().required('Villa number is required'),
        }),
      } : {}),
    }),
  },
  {
    title: 'Your Connection',
    subtitle: 'Step 2 of 4',
    fields: ['connection', 'connectionOther'],
    schema: Yup.object().shape({}),
  },
  {
    title: 'Your Interests',
    subtitle: 'Step 3 of 4',
    fields: ['interests', ...(fieldPresence.stayInvolved ? ['stayInvolved'] : [])],
    schema: Yup.object().shape({
      interests: Yup.array().of(Yup.string()).min(1, 'Please select at least one option'),
      ...(fieldPresence.stayInvolved && STAY_INVOLVED_REQUIRED ? {
        stayInvolved: Yup.array().of(Yup.string()).min(1, 'Please select at least one option'),
      } : {}),
    }),
  },
  {
    title: 'Almost Done',
    subtitle: 'Step 4 of 4',
    // Anjela's consent reuses the `privacy` field (see StepPrivacy) rather than validating a
    // separate `projectConsent` field, so there's only ever one required checkbox here.
    fields: ['privacy'],
    schema: Yup.object().shape({
      privacy: Yup.boolean().oneOf(
        [true],
        fieldPresence.projectConsent ? 'You must accept to continue' : 'You must accept the privacy terms',
      ),
    }),
  },
];

const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  connection: [],
  connectionOther: '',
  interests: [],
  privacy: false,
  isResident: '',
  villaNumber: '',
  stayInvolved: [],
};

// Step 1: Contact Details
const StepDetails = ({ fieldPresence }: { fieldPresence: ResidentFieldPresence }) => {
  const { values, errors, touched, setFieldValue } = useFormikContext<FormValues>();

  return (
    <>
      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label htmlFor="firstName">First name *</label>
          <FormikField type="text" name="firstName" placeholder="First name" required />
        </div>
        <div className={styles.field}>
          <label htmlFor="lastName">Last name *</label>
          <FormikField type="text" name="lastName" placeholder="Last name" required />
        </div>
      </div>
      <div className={styles.field}>
        <label htmlFor="email">Email *</label>
        <FormikField type="email" name="email" placeholder="Email address" required />
      </div>
      <div className={styles.field}>
        <label htmlFor="phone">Phone (optional)</label>
        <FormikField as="input" type="tel" name="phone" placeholder="Phone number" />
      </div>
      {fieldPresence.isResident && (
        <div className={styles.field}>
          <fieldset className={styles.radioFieldset}>
            <legend>{IS_RESIDENT_LABEL} *</legend>
            <div className={styles.radioGroup}>
              <label className={styles.radio}>
                <input
                  type="radio"
                  name="isResident"
                  value="Yes"
                  checked={values.isResident === 'Yes'}
                  onChange={() => setFieldValue('isResident', 'Yes')}
                />
                <span className={styles.radioLabel}>Yes</span>
              </label>
              <label className={styles.radio}>
                <input
                  type="radio"
                  name="isResident"
                  value="No"
                  checked={values.isResident === 'No'}
                  onChange={() => {
                    setFieldValue('isResident', 'No');
                    setFieldValue('villaNumber', '');
                  }}
                />
                <span className={styles.radioLabel}>No</span>
              </label>
            </div>
          </fieldset>
          {errors.isResident && touched.isResident && (
            <span className={styles.error} role="alert">{errors.isResident}</span>
          )}
        </div>
      )}
      {fieldPresence.villaNumber && values.isResident === 'Yes' && (
        <div className={styles.field}>
          <label htmlFor="villaNumber">{VILLA_NUMBER_LABEL} *</label>
          {/* No native `required` here (unlike firstName/lastName/email): with isResident==='Yes'
              gating this field's render, a native constraint-validation tooltip would block
              Formik's submit handler from running at all, so the Yup `.when(...)` error below
              (styles.error) never gets a chance to render. The visible "*" above is plain label
              text (see FormikField — it doesn't derive any asterisk from `required`), so omitting
              the attribute here doesn't change what's shown. */}
          <FormikField type="text" name="villaNumber" placeholder={VILLA_NUMBER_LABEL} />
          {errors.villaNumber && touched.villaNumber && (
            <span className={styles.error} role="alert">{errors.villaNumber}</span>
          )}
        </div>
      )}
    </>
  );
};

// Step 2: Connection to Project
const StepConnection = ({ connectionOptions }: { connectionOptions: string[] }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  return (
    <>
      <p className={styles.stepDescription}>Select all that apply</p>
      <div className={styles.checkboxGroup}>
        {connectionOptions.map((option, i) => (
          <label key={`${i}-${option}`} className={styles.checkbox}>
            <input
              type="checkbox"
              checked={values.connection.includes(option)}
              onChange={(e) => {
                const next = e.target.checked
                  ? [...values.connection, option]
                  : values.connection.filter((v) => v !== option);
                setFieldValue('connection', next);
              }}
            />
            <span className={styles.checkboxLabel}>{option}</span>
          </label>
        ))}
        <div className={styles.otherField}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={values.connectionOther.length > 0}
              onChange={(e) => {
                if (!e.target.checked) setFieldValue('connectionOther', '');
              }}
            />
            <span className={styles.checkboxLabel}>Other:</span>
          </label>
          <input
            type="text"
            className={styles.otherInput}
            value={values.connectionOther}
            onChange={(e) => setFieldValue('connectionOther', e.target.value)}
            placeholder="Please specify"
          />
        </div>
      </div>
    </>
  );
};

// Step 3: Interests
const StepInterests = ({
  fieldPresence,
  interestOptions,
  stayInvolvedOptions,
}: {
  fieldPresence: ResidentFieldPresence;
  interestOptions: string[];
  stayInvolvedOptions: string[];
}) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<FormValues>();
  return (
    <>
      <p className={styles.stepDescription}>Select all that apply *</p>
      {errors.interests && touched.interests && (
        <span className={styles.error}>{errors.interests}</span>
      )}
      <div className={styles.checkboxGroup}>
        {interestOptions.map((option, i) => (
          <label key={`${i}-${option}`} className={styles.checkbox}>
            <input
              type="checkbox"
              checked={values.interests.includes(option)}
              onChange={(e) => {
                const next = e.target.checked
                  ? [...values.interests, option]
                  : values.interests.filter((v) => v !== option);
                setFieldValue('interests', next);
              }}
            />
            <span className={styles.checkboxLabel}>{option}</span>
          </label>
        ))}
      </div>
      {fieldPresence.stayInvolved && (
        <div className={styles.stayInvolvedGroup}>
          <p className={styles.stepDescription}>{STAY_INVOLVED_LABEL}</p>
          {errors.stayInvolved && touched.stayInvolved && (
            <span className={styles.error}>{errors.stayInvolved}</span>
          )}
          <div className={styles.checkboxGroup}>
            {stayInvolvedOptions.map((option, i) => (
              <label key={`${i}-${option}`} className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={values.stayInvolved.includes(option)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...values.stayInvolved, option]
                      : values.stayInvolved.filter((v) => v !== option);
                    setFieldValue('stayInvolved', next);
                  }}
                />
                <span className={styles.checkboxLabel}>{option}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// Step 4: Privacy & Submit
const StepPrivacy = ({
  privacyEmail,
  fieldPresence,
}: {
  privacyEmail?: string | null;
  fieldPresence: ResidentFieldPresence;
}) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<FormValues>();
  return (
    <>
      <label className={cc({ [styles.checkbox]: true, [styles.privacyCheckbox]: true })}>
        <input
          type="checkbox"
          checked={values.privacy}
          onChange={(e) => setFieldValue('privacy', e.target.checked)}
        />
        <span className={styles.checkboxLabel}>
          {fieldPresence.projectConsent ? (
            <>
              {PROJECT_CONSENT_STATEMENT}
              <span className={styles.consentClarification}>{PROJECT_CONSENT_CLARIFICATION}</span>
            </>
          ) : (
            <>
              By submitting this form, you consent to Amana Living collecting and using your
              information to provide project updates, invitations to engagement activities and, if
              selected, information about future accommodation availability. Your details will only
              be accessed by Amana Living and authorised representatives for these purposes and
              will not be shared with third parties unless required by law.
              {privacyEmail && (
                <> To update or remove your information, contact {privacyEmail}.</>
              )}
            </>
          )}
        </span>
      </label>
      {errors.privacy && touched.privacy && (
        <span className={styles.error}>{errors.privacy}</span>
      )}
    </>
  );
};

const STEP_COMPONENTS = [StepDetails, StepConnection, StepInterests, StepPrivacy];

interface MenorahFormProps {
  formHandle?: string | null;
  privacyEmail?: string | null;
  locationName?: string | null;
  // CMS-provided checkbox options; each falls back to the built-in list when undefined.
  connectionOptions?: string[];
  interestOptions?: string[];
  stayInvolvedOptions?: string[];
}

const MenorahForm = ({
  formHandle,
  privacyEmail,
  locationName,
  connectionOptions,
  interestOptions,
  stayInvolvedOptions,
}: MenorahFormProps) => {
  const globals = useStore('globals');
  const handle = formHandle || '';
  const freeformObj = globals?.freeform?.find((form) => form.handle === handle);
  const place = locationName || 'this area';
  const registrationLabel = `${place} Registration`;
  const fieldPresence = getResidentFieldPresence(freeformObj);
  const effectiveConnectionOptions = connectionOptions?.length ? connectionOptions : getConnectionOptions(place);
  const effectiveInterestOptions = interestOptions?.length ? interestOptions : INTEREST_OPTIONS;
  const effectiveStayInvolvedOptions = stayInvolvedOptions?.length ? stayInvolvedOptions : STAY_INVOLVED_OPTIONS;

  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [errored, setErrored] = useState(false);

  const steps = getSteps(fieldPresence);
  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;
  const StepComponent = STEP_COMPONENTS[step];

  async function handleSubmit(values: FormValues, helpers: FormikHelpers<FormValues>) {
    if (!isLastStep) {
      helpers.setTouched({});
      helpers.setSubmitting(false);
      setStep(step + 1);
      return;
    }

    const formData = new FormData();
    formData.append('firstName', values.firstName);
    formData.append('lastName', values.lastName);
    formData.append('email', values.email);
    formData.append('phone', values.phone);

    const connections = [...values.connection];
    if (values.connectionOther.trim()) {
      connections.push(`Other: ${values.connectionOther.trim()}`);
    }
    formData.append('connection', connections.join(', '));
    formData.append('interests', values.interests.join(', '));

    // Optional "resident" fields (SAJOB-6530) — only appended when the current form's
    // Freeform layout has them, so unrelated forms (e.g. Menora) get a byte-identical payload.
    if (fieldPresence.isResident) {
      formData.append('isResident', values.isResident);
    }
    if (fieldPresence.villaNumber && values.isResident === 'Yes') {
      formData.append('villaNumber', values.villaNumber);
    }
    if (fieldPresence.stayInvolved) {
      formData.append('stayInvolved', values.stayInvolved.join(', '));
    }
    if (fieldPresence.projectConsent) {
      // Anjela's consent replaces the generic privacy checkbox (see StepPrivacy), so its
      // acceptance lives in `privacy`; the step-4 Yup schema already requires `privacy` to be
      // true before submit can reach here, so this is always 'Yes' in practice. Posting the
      // literal rather than `values.privacy ? 'Yes' : ''` keeps that guarantee explicit. If
      // projectConsent ever needs to become optional (open question #2), it will need its own
      // boolean field, separate from `privacy` — this line can't just start reading `false`.
      formData.append('projectConsent', 'Yes');
    }

    formData.append('handle', handle);
    formData.append('origin', 'Amana Living');

    // Honeypot — must be included in FormData for Freeform spam check
    if (freeformObj?.honeypot?.name) {
      formData.append(freeformObj.honeypot.name, '');
    }

    const response = await FreeformHelper.sendForm(formData, freeformObj);

    if (response && response.success) {
      setSubmitted(true);
      trackEvent(registrationLabel, 'Successful Submission', document.title);
    } else {
      setErrored(true);
      setExtra('errors', response?.errors);
      captureException(`${registrationLabel} form submission failed`);
    }

    helpers.setSubmitting(false);
  }

  if (submitted) {
    return (
      <div className={styles.form}>
        <div className={styles.message}>
          <div className={styles.successIcon}>&#10003;</div>
          <h3>Thank you!</h3>
          <p>Your details have been received, we will be in touch soon!</p>
        </div>
      </div>
    );
  }

  if (errored) {
    return (
      <div className={styles.form}>
        <div className={styles.message}>
          <h3>Something went wrong</h3>
          <p>An error occurred while submitting your registration. Please try again.</p>
          <Button type="orangeBorder" onClick={() => { setErrored(false); setStep(0); }}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={currentStep.schema}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur
    >
      {({ handleSubmit, isSubmitting }) => (
        <form
          onSubmit={handleSubmit}
          className={cc({ [styles.form]: true, [styles.submitting]: isSubmitting })}
        >
          {/* Progress bar */}
          <div className={styles.progressBar}>
            {steps.map((_, i) => (
              <div
                key={i}
                className={cc({
                  [styles.progressSegment]: true,
                  [styles.progressDone]: i < step,
                  [styles.progressActive]: i === step,
                })}
              />
            ))}
          </div>

          {/* Step header */}
          <div className={styles.stepHeader}>
            <span className={styles.stepLabel}>{currentStep.subtitle}</span>
            <h3 className={styles.formTitle}>Register to Stay Informed</h3>
            <p className={styles.stepSubtitle}>{currentStep.title}</p>
          </div>

          {/* Step content */}
          <div className={styles.stepContent}>
            <StepComponent
              privacyEmail={privacyEmail}
              fieldPresence={fieldPresence}
              connectionOptions={effectiveConnectionOptions}
              interestOptions={effectiveInterestOptions}
              stayInvolvedOptions={effectiveStayInvolvedOptions}
            />
          </div>

          {/* Honeypot */}
          {isLastStep && freeformObj?.honeypot && (
            <input type="hidden" name={freeformObj.honeypot.name || ''} value={freeformObj.honeypot.value || ''} />
          )}

          {/* Navigation */}
          <div className={styles.stepNav}>
            {step > 0 && (
              <button
                type="button"
                className={styles.backButton}
                onClick={() => setStep(step - 1)}
              >
                ← Back
              </button>
            )}
            <Button
              as="submit"
              disabled={isSubmitting}
              type="solidOrange"
              large
              className={styles.nextButton}
            >
              {isLastStep ? 'Register' : 'Next'}
            </Button>
          </div>

          <div className={cc({ [styles.spinner]: true, [styles.visible]: isSubmitting })}>
            <Spinner size="xlarge" isCompleting={!isSubmitting} />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default MenorahForm;
